<template>
  <div class="image-upload-container">
    <el-upload
      ref="upload"
      action="#"
      :file-list="fileList"
      list-type="picture-card"
      :auto-upload="false"
      :multiple="true"
      :limit="maxFiles"
      :accept="accept"
      :before-upload="beforeUpload"
      :on-change="handleChange"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :on-exceed="handleExceed"
      :class="{ 'is-disabled': disabled }"
    >
      <div class="upload-trigger" v-if="!disabled">
        <i class="el-icon-plus"></i>
      </div>
    </el-upload>

    <!-- 图片预览弹窗 -->
    <el-dialog
      :visible.sync="previewVisible"
      title="图片预览"
      width="60%"
      append-to-body
      :modal-append-to-body="false"
    >
      <div class="preview-container">
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ImageUpload',
  props: {
    // 当前图片列表
    value: {
      type: Array,
      default: () => []
    },
    // 最大上传文件数量
    maxFiles: {
      type: Number,
      default: 5
    },
    // 最大文件大小（MB）
    maxSize: {
      type: Number,
      default: 10
    },
    // 接受的文件类型
    accept: {
      type: String,
      default: '.jpg,.jpeg,.png,.gif,.bmp,.webp'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 上传接口地址（如果需要实时上传）
    uploadUrl: {
      type: String,
      default: ''
    },
    // 上传请求头
    uploadHeaders: {
      type: Object,
      default: () => ({})
    },
    // 上传附加数据
    uploadData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      fileList: [],
      previewVisible: false,
      previewImageUrl: '',
      updateTimer: null
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (Array.isArray(newVal)) {
          const mappedList = newVal.map((item, index) => ({
            uid: item.uid || Date.now() + index,
            name: item.name || `图片${index + 1}`,
            url: item.url || item.preview || '',
            status: 'success',
            file: item.file || null
          }));
          
          // 避免不必要的更新
          if (JSON.stringify(this.fileList) !== JSON.stringify(mappedList)) {
            this.fileList = mappedList;
          }
        }
      },
      immediate: true,
      deep: true
    },
    fileList: {
      handler(newVal) {
        // 防抖处理，避免频繁更新
        if (this.updateTimer) {
          clearTimeout(this.updateTimer);
        }
        
        this.updateTimer = setTimeout(() => {
          const files = newVal.map(item => ({
            uid: item.uid,
            name: item.name,
            url: item.url,
            file: item.file || null,
            preview: item.url
          }));
          
          this.$emit('input', files);
          this.$emit('change', files);
        }, 100);
      },
      deep: true
    }
  },
  methods: {
    /**
     * 上传前检查
     */
    beforeUpload(file) {
      // 检查文件类型
      const isValidType = this.accept.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      if (!isValidType) {
        this.$message.error(`上传图片只能是 ${this.accept} 格式!`);
        return false;
      }

      // 检查文件大小
      const isValidSize = file.size / 1024 / 1024 < this.maxSize;
      if (!isValidSize) {
        this.$message.error(`上传图片大小不能超过 ${this.maxSize}MB!`);
        return false;
      }

      return true;
    },

    /**
     * 文件列表变化
     */
    handleChange(file, fileList) {
      console.log('=== handleChange 被调用 ===');
      console.log('当前操作的文件:', {
        name: file.name,
        status: file.status,
        hasRaw: !!file.raw,
        uid: file.uid
      });
      console.log('fileList 长度:', fileList.length);
      
      // 处理新添加的文件
      if (file.status === 'ready' && file.raw) {
        console.log('检测到新文件，开始处理:', file.name);
        
        // 更新fileList为包含原始文件的版本
        this.fileList = fileList.map(item => {
          if (item.uid === file.uid) {
            console.log('为文件添加原始文件对象:', item.name);
            return {
              ...item,
              file: file.raw,  // 保存原始文件
              status: 'ready'
            };
          }
          return item;
        });
        
        console.log('handleChange 完成，fileList状态:', this.fileList.map(item => ({
          name: item.name,
          hasFile: !!item.file,
          status: item.status,
          hasUrl: !!item.url
        })));
        
        // 立即生成预览
        this.generatePreview(file);
      } else {
        console.log('常规文件列表更新，状态:', file.status);
        // 保持现有的file属性
        const updatedList = fileList.filter(f => f.status !== 'fail').map(item => {
          const existing = this.fileList.find(e => e.uid === item.uid);
          return {
            ...item,
            file: existing?.file || null
          };
        });
        this.fileList = updatedList;
      }
    },

    /**
     * 生成图片预览
     */
    generatePreview(file) {
      console.log('generatePreview 被调用:', {
        fileName: file.name,
        hasRaw: !!file.raw,
        hasUrl: !!file.url,
        status: file.status
      });
      
      if (!file.raw) {
        console.log('跳过预览生成：没有原始文件');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('FileReader 读取完成，开始更新预览');
        const index = this.fileList.findIndex(f => f.uid === file.uid);
        console.log('找到文件索引:', index);
        
        if (index !== -1) {
          const currentItem = this.fileList[index];
          console.log('更新前的文件项:', {
            hasUrl: !!currentItem.url,
            hasFile: !!currentItem.file,
            status: currentItem.status,
            name: currentItem.name
          });
          
          // 更新预览URL，同时保持文件对象
          this.$set(this.fileList, index, {
            ...currentItem,
            url: e.target.result,
            status: 'success',  // 设置为成功状态
            file: currentItem.file || file.raw  // 优先保持现有文件，否则使用新文件
          });
          
          console.log('generatePreview 更新后的文件项:', {
            hasUrl: !!this.fileList[index].url,
            hasFile: !!this.fileList[index].file,
            status: this.fileList[index].status,
            urlLength: this.fileList[index].url?.length || 0
          });
        }
      };
      
      reader.onerror = (error) => {
        console.error('读取文件失败:', error);
        this.$message.error('读取图片文件失败');
      };
      
      try {
        console.log('开始读取文件:', file.raw.name, file.raw.size);
        reader.readAsDataURL(file.raw);
      } catch (error) {
        console.error('FileReader error:', error);
        this.$message.error('图片预览生成失败');
      }
    },

    /**
     * 移除文件
     */
    handleRemove(file, fileList) {
      this.fileList = fileList;
    },

    /**
     * 预览图片
     */
    handlePreview(file) {
      this.previewImageUrl = file.url;
      this.previewVisible = true;
    },

    /**
     * 超出数量限制
     */
    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传 ${this.maxFiles} 张图片，当前共选择了 ${files.length + fileList.length} 张图片`);
    },

    /**
     * 手动上传
     */
    submit() {
      this.$refs.upload.submit();
    },

    /**
     * 清空文件列表
     */
    clearFiles() {
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.$refs.upload.clearFiles();
      this.fileList = [];
    },

    /**
     * 获取文件数据用于提交
     */
    getFilesForSubmit() {
      console.log('ImageUpload - 获取文件用于提交:');
      console.log('当前fileList长度:', this.fileList.length);
      
      // 详细检查每个文件项
      this.fileList.forEach((item, index) => {
        console.log(`文件项 ${index}:`, {
          uid: item.uid,
          name: item.name,
          url: item.url,
          status: item.status,
          hasFile: !!item.file,
          fileType: typeof item.file,
          file: item.file
        });
      });
      
      const files = this.fileList.filter(file => file.file).map(file => file.file);
      console.log('过滤后的文件数量:', files.length);
      console.log('过滤后的文件:', files);
      
      return files;
    }
  },
  
  beforeDestroy() {
    // 清理定时器
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
  }
};
</script>

<style scoped>
.image-upload-container {
  width: 100%;
}

.upload-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c939d;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-trigger:hover {
  color: #409EFF;
}

.upload-trigger .el-icon-plus {
  font-size: 24px;
}

.is-disabled .upload-trigger {
  color: #c0c4cc;
  cursor: not-allowed;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 70vh;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 修改ElementUI默认样式 */
.image-upload-container >>> .el-upload--picture-card {
  width: 80px;
  height: 80px;
  line-height: 82px;
}

.image-upload-container >>> .el-upload-list--picture-card .el-upload-list__item {
  width: 80px;
  height: 80px;
}

.image-upload-container >>> .el-upload-list--picture-card .el-upload-list__item-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-upload-container >>> .el-upload-list--picture-card .el-upload-list__item-actions {
  font-size: 16px;
}

.image-upload-container.is-disabled >>> .el-upload--picture-card {
  display: none;
}
</style>
